import CoreService from "@/src/modules/core/services/core";
import {meService} from "@/src/modules/core/services/me";
import {useMeStore} from "@/src/modules/core/store/me";
import {usePanicIntentStore} from "@/src/modules/panic-alerts/store/panic-intent";
import type {ParamsOptions} from "@/src/modules/core/types";

import {panicAlertsApi} from "../api";
import type {
  PanicAlertFilters,
  PanicAlertUpdatePayload,
  PanicConfirmPayload,
  PanicIntentPayload,
} from "../types";

class PanicAlertsService extends CoreService {
  getCanTrigger(): boolean {
    return meService.getHasPermission("panic_alert store");
  }

  getCanShow(): boolean {
    return meService.getHasPermission("panic_alert show");
  }

  /** Main nav + `/panic-alerts` page — requires company panic enabled (same as FAB boot gating). */
  getCanShowSection(): boolean {
    if (!this.getCanShow()) {
      return false;
    }
    const me = useMeStore.getState().me;
    return me?.company?.panic_button?.enabled === true;
  }

  getCanUpdate(): boolean {
    return meService.getHasPermission("panic_alert update");
  }

  getCanViewStats(): boolean {
    return meService.getHasPermission("panic_alert statistics");
  }

  getCanRespond(): boolean {
    if (!this.getCanUpdate()) {
      return false;
    }
    const me = useMeStore.getState().me;
    if (!me) {
      return false;
    }
    return (
      me.isOfficer ||
      me.isPatrolOfficer ||
      me.isPostCommander ||
      me.isSupervisor ||
      me.isSeniorSupervisor ||
      me.isDispatch ||
      me.isDispatchSupervisor ||
      me.isAdmin
    );
  }

  async getConfig(propertyId?: number) {
    const {data} = await panicAlertsApi.getConfig(propertyId);
    return data.data;
  }

  async createIntent(payload: PanicIntentPayload) {
    const {data} = await panicAlertsApi.intent(payload);
    const intent = data.data;
    usePanicIntentStore.getState().setIntent({
      intentToken: intent.intent_token,
      expiresAt: intent.expires_at,
      confirmationWindowSeconds: intent.confirmation_window_seconds,
      alertId: intent.panic_alert_intent_id,
    });
    return intent;
  }

  async confirmIntent(payload: PanicConfirmPayload) {
    const {data} = await panicAlertsApi.confirm(payload);
    usePanicIntentStore.getState().clearIntent();
    return data.data;
  }

  async cancelIntent(intentToken: string) {
    const {data} = await panicAlertsApi.cancel(intentToken);
    usePanicIntentStore.getState().clearIntent();
    return data.data;
  }

  getStoredIntent() {
    return usePanicIntentStore.getState();
  }

  clearStoredIntent() {
    usePanicIntentStore.getState().clearIntent();
  }

  async list(options?: ParamsOptions) {
    const {data} = await panicAlertsApi.list(options);
    return data;
  }

  async show(id: number) {
    const {data} = await panicAlertsApi.show(id);
    return data.data;
  }

  async patch(id: number, payload: PanicAlertUpdatePayload) {
    const {data} = await panicAlertsApi.patchAlert(id, payload);
    return data.data;
  }

  async statistics() {
    const {data} = await panicAlertsApi.getStatistics();
    return data.data;
  }

  buildListFilterSummary(filters: PanicAlertFilters) {
    return Object.values(filters).filter((value) => value != null && value !== "").length;
  }
}

export const panicAlertsService = new PanicAlertsService();
